/**
 * Salud del sitio — un solo lugar que mide, dice qué hay y deja arreglarlo.
 *
 * Reemplaza cuatro contenedores que medían lo mismo por separado: SEO del
 * borrador, accesibilidad, SEO del sitio publicado y "qué arreglar". Eduardo
 * (2026-09-06): *"hoy hay seis contenedores que miden y uno que arregla. Lo que
 * necesito es al revés"*.
 *
 * **La fuente no organiza la pantalla.** Se ve como etiqueta, porque importa
 * para entender el hallazgo, pero ordenar por fuente es ordenar por cómo está
 * construido EvoHub y no por lo que hay que hacer. Lo que ordena es `accion`,
 * que decide el servidor.
 *
 * Un origen que no se midió se dice con su motivo, y NO aporta cero hallazgos:
 * "no hay problemas de velocidad" y "no medí la velocidad" no pueden verse
 * igual.
 *
 * ── Layout (handoff paso3-1b, 2026-09-06) ────────────────────────────────────
 *
 * Tres bandas dentro de la misma tarjeta: los puntajes en una banda de tres
 * celdas (el motivo de una fuente sin medir va DENTRO de su celda, que es donde
 * se busca), la barra de trabajo con cuántas cosas hay y los dos botones del
 * lote, y la lista con una columna de acción de ancho fijo — así todos los
 * botones caen en la misma vertical. Las instrucciones largas quedan plegadas
 * detrás de «Cómo funciona».
 */
import { useCallback, useRef, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { SectionHeaderAction } from '../../ui/SectionHeaderAction.js';
import { Button } from '../../ui/Button.js';
import { LoadingState } from '../../ui/LoadingState.js';
import { useToast } from '../../ui/Toast.js';
import { useRunAudit } from '../../../hooks/useProjects.js';
import { useHallazgos, useRestaurarHallazgo } from '../../../hooks/useHallazgos.js';
import type { RespuestaHallazgos, Origen } from '../../../hooks/useHallazgos.js';
import { FilaHallazgo, REJILLA_DE_FILA } from './FilaHallazgo.js';
import type { ApiDeFila } from './FilaHallazgo.js';

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

const etiqueta = {
  ...menor,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
} as const;

/** El color del numeral dice cuán bien está: igual que las pills de reportes. */
function colorDePuntaje(v: number | null): string {
  if (v == null) return 'var(--evo-fg-4)';
  if (v >= 90) return 'var(--evo-cyan)';
  if (v >= 60) return 'var(--evo-amber)';
  return 'var(--evo-danger)';
}

function Grupo({ titulo, nota, children }: { titulo: string; nota?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--evo-space-3)' }}>
      <div style={{ ...etiqueta, marginBottom: 'var(--evo-space-1)' }}>{titulo}</div>
      {nota && <p style={{ ...menor, margin: '0 0 var(--evo-space-2)', maxWidth: '76ch' }}>{nota}</p>}
      {children}
    </div>
  );
}

const FUENTES = ['accesibilidad', 'seo', 'velocidad'] as const;
const CLAVE_BANDA = { accesibilidad: 'accessibility', seo: 'seo', velocidad: 'speed' } as const;

/** Una celda de la banda: etiqueta, numeral, barra y de dónde salió. */
function CeldaDePuntaje({ fuente, valor, origen, ultima }: {
  fuente: typeof FUENTES[number];
  valor: number | null;
  origen: Origen | undefined;
  ultima: boolean;
}) {
  const { t, i18n } = useTranslation();
  const color = colorDePuntaje(valor);
  // Con la medición guardada, la fecha: "sitio publicado · medido el 6 sep".
  const contexto = origen && !origen.medido
    ? origen.porque
    : fuente === 'accesibilidad'
      ? t('projects.design.tab.step3.health.band.draft')
      : `${t('projects.design.tab.step3.health.band.live')}${origen?.medido && origen.cuando ? ` · ${t('projects.design.tab.step3.health.measuredOn', { fecha: new Date(origen.cuando).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }) })}` : ''}`;
  return (
    <div style={{
      padding: 'var(--evo-space-3) var(--evo-space-4)',
      borderRight: ultima ? 'none' : '1px solid var(--evo-stroke)',
      display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', minWidth: 0,
    }}>
      <div style={etiqueta}>{t(`projects.design.tab.step3.health.band.${CLAVE_BANDA[fuente]}`)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-1)' }}>
        <span style={{ fontSize: 'var(--evo-fs-puntaje)', fontWeight: 'var(--evo-fw-light)', lineHeight: 1, color, fontFamily: 'var(--evo-font)' }}>
          {valor == null ? '—' : String(valor)}
        </span>
        {valor != null && <span style={menor}>/100</span>}
      </div>
      <div style={{ height: 'var(--evo-meter-h)', background: 'var(--evo-bg-2)', overflow: 'hidden' }}>
        <div style={{ width: `${String(Math.max(0, Math.min(100, valor ?? 0)))}%`, height: '100%', background: color }} />
      </div>
      <div style={{ ...menor, color: origen && !origen.medido ? 'var(--evo-amber)' : 'var(--evo-fg-4)' }}>
        {contexto}
      </div>
      {/* El porqué del número, en una línea: cuánto tardó. No son tareas. */}
      {fuente === 'velocidad' && origen?.medido && origen.metricas && (
        <div style={{ ...menor, color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
          {(['lcp', 'fcp', 'tbt', 'cls'] as const)
            .filter(k => origen.metricas?.[k])
            .map(k => `${t(`projects.design.tab.step3.health.metrics.${k}`)} ${origen.metricas?.[k] ?? ''}`)
            .join(' · ')}
        </div>
      )}
    </div>
  );
}

export function SaludDelSitio({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();

  // Arranca sin velocidad: PageSpeed gasta cuota diaria y abrir la pestaña de
  // Diseño para mirar otra cosa no tiene por qué consumirla. El botón la prende.
  const [conVelocidad, setConVelocidad] = useState(false);
  // El diagnóstico va plegado. Son mediciones del servidor del cliente, no
  // tareas de Eduardo, y desplegadas entierran las tareas: en Evo Creative
  // eran 21 filas de Lighthouse sobre 7 cosas para hacer.
  const [verDiagnostico, setVerDiagnostico] = useState(false);
  const [verComoFunciona, setVerComoFunciona] = useState(false);
  const { data, isLoading, isFetching, refetch } = useHallazgos(projectId, conVelocidad);
  const correrAuditoria = useRunAudit(projectId);

  const midiendo = correrAuditoria.isPending || isFetching;

  // ── El lote ────────────────────────────────────────────────────────────────
  // Siete hallazgos que necesitan texto son siete clics de "redactar" y siete
  // de "aplicar". Eduardo (2026-09-06): "¿qué maldita proposición de texto? Yo
  // no tengo que sugerir nada". Cada fila se anota acá y el lote las recorre
  // de a una — en serie, no en paralelo: son siete llamadas al modelo.
  const filas = useRef(new Map<string, ApiDeFila>());
  const registrar = useCallback((id: string, api: ApiDeFila) => {
    filas.current.set(id, api);
    return () => { filas.current.delete(id); };
  }, []);
  const [lote, setLote] = useState<{ que: 'redactar' | 'aplicar'; n: number; total: number } | null>(null);
  const [cuenta, setCuenta] = useState({ sinBorrador: 0, conBorrador: 0 });
  // Lo que el lote dejó, contado por estado. Sin esto, "redactar los 3"
  // terminaba y la pantalla no decía qué había pasado con cada uno.
  const [resultadoDelLote, setResultadoDelLote] = useState<{ propuestas: number; bien: number; decidir: number; errores: number } | null>(null);
  const [verDescartados, setVerDescartados] = useState(false);
  const restaurar = useRestaurarHallazgo(projectId);
  const recontar = useCallback(() => {
    const todas = Array.from(filas.current.values());
    setCuenta({
      sinBorrador: todas.filter(f => f.accion === 'texto' && f.estado() === 'inicial').length,
      conBorrador: todas.filter(f => f.tieneBorrador()).length,
    });
  }, []);

  async function redactarTodos(): Promise<void> {
    // Una fila ya aplicada no se vuelve a redactar: el lote la volvía a abrir
    // con la misma propuesta y parecía que no había pasado nada.
    const pendientes = Array.from(filas.current.values()).filter(f => f.accion === 'texto' && f.estado() === 'inicial');
    for (let i = 0; i < pendientes.length; i++) {
      setLote({ que: 'redactar', n: i + 1, total: pendientes.length });
      await pendientes[i]!.redactar();
    }
    setLote(null);
    const estados = pendientes.map(f => f.estado());
    setResultadoDelLote({
      propuestas: estados.filter(e => e === 'propuesta').length,
      bien: estados.filter(e => e === 'bien').length,
      decidir: estados.filter(e => e === 'decidir').length,
      errores: estados.filter(e => e === 'error').length,
    });
    recontar();
  }

  async function aplicarTodos(): Promise<void> {
    const listos = Array.from(filas.current.values()).filter(f => f.tieneBorrador());
    for (let i = 0; i < listos.length; i++) {
      setLote({ que: 'aplicar', n: i + 1, total: listos.length });
      await listos[i]!.aplicar();
    }
    setLote(null);
    recontar();
  }

  function medirTodo(): void {
    // La accesibilidad es lo único que hay que CORRER: abre un Chromium y por
    // eso no pasa sola. El SEO del borrador se recalcula en cada consulta —
    // cuesta milisegundos— y la velocidad entra al prender el interruptor.
    correrAuditoria.mutate('local', {
      onSettled: () => {
        setConVelocidad(true);
        void refetch();
      },
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }

  const cabecera = (
    <SectionHeaderAction loading={midiendo} onClick={medirTodo}>
      {midiendo ? t('projects.design.tab.step3.health.measuring') : t('projects.design.tab.step3.health.measure')}
    </SectionHeaderAction>
  );

  if (isLoading) {
    return (
      <DetailSection title={t('projects.design.tab.step3.health.title')} titleRight={cabecera}>
        <LoadingState size="sm" />
      </DetailSection>
    );
  }

  const d: RespuestaHallazgos | undefined = data;
  // Para hacer: lo que tiene botón. Lo bloqueado no es una tarea, es un dato:
  // va abajo con los diagnósticos (Eduardo, 2026-09-09: "me sigue saliendo un
  // to-do que no puedo ajustar").
  const paraHacer = (d?.hallazgos ?? []).filter(h => h.accion === 'automatico' || h.accion === 'texto');
  const paraSaber = (d?.hallazgos ?? []).filter(h => h.accion === 'diagnostico' || h.accion === 'bloqueado');
  const sinMedir = (d?.origenes ?? []).filter(o => !o.medido);
  const todoSinMedir = sinMedir.length === (d?.origenes.length ?? 0);
  // La auditoría de accesibilidad de la que salieron los hallazgos: es lo que
  // el botón "arreglar" de cada fila le manda al agente.
  const accesibilidad = (d?.origenes ?? []).find(o => o.fuente === 'accesibilidad');
  const auditId = accesibilidad?.medido ? accesibilidad.auditId : undefined;
  const origenDe = (f: typeof FUENTES[number]) => (d?.origenes ?? []).find(o => o.fuente === f);

  return (
    <DetailSection title={t('projects.design.tab.step3.health.title')} titleRight={cabecera} padding="none">
      {/* Los tres puntajes en una banda. Sin medir muestra un guion y la barra
          en cero — nunca un cero, que diría que el sitio sacó cero. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', borderBottom: '1px solid var(--evo-stroke)' }}>
        {FUENTES.map((f, i) => (
          <CeldaDePuntaje key={f} fuente={f} valor={d?.puntajes[f] ?? null} origen={origenDe(f)} ultima={i === FUENTES.length - 1} />
        ))}
      </div>

      {d && d.hallazgos.length > 0
        ? (
          <>
            {/* La barra de trabajo: cuánto hay, los dos botones del lote, y las
                instrucciones plegadas. Un botón que dice "Proponer texto" sin
                decir qué pasa después enseña a no apretarlo (Eduardo: "¿qué
                significa? ¿qué hago con eso?") — por eso «Cómo funciona». */}
            <div style={{
              padding: 'var(--evo-space-3) var(--evo-space-4)',
              background: 'var(--evo-panel)',
              borderBottom: '1px solid var(--evo-stroke)',
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)',
            }}>
              <div style={{ flex: 1, minWidth: '12em', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
                  {t('projects.design.tab.step3.health.toDoCount', { count: paraHacer.length })}
                </div>
                <div style={menor}>{t('projects.design.tab.step3.health.breakdown', { ...d.resumen })}</div>
              </div>
              {cuenta.sinBorrador > 0 && (
                <Button size="xs" tone="cyan" loading={lote?.que === 'redactar'} disabled={lote !== null} onClick={() => { void redactarTodos(); }}>
                  {lote?.que === 'redactar'
                    ? t('projects.design.tab.step3.health.draftingN', { n: lote.n, total: lote.total })
                    : t('projects.design.tab.step3.health.draftAll', { count: cuenta.sinBorrador })}
                </Button>
              )}
              {cuenta.conBorrador > 0 && (
                <Button size="xs" variant="outline" tone="cyan" loading={lote?.que === 'aplicar'} disabled={lote !== null} onClick={() => { void aplicarTodos(); }}>
                  {lote?.que === 'aplicar'
                    ? t('projects.design.tab.step3.health.applyingN', { n: lote.n, total: lote.total })
                    : t('projects.design.tab.step3.health.applyAll', { count: cuenta.conBorrador })}
                </Button>
              )}
              <Button size="xs" variant="link" tone="muted" onClick={() => { setVerComoFunciona(v => !v); }}>
                {t('projects.design.tab.step3.health.howItWorks')} {verComoFunciona ? '▴' : '▾'}
              </Button>
            </div>
            {verComoFunciona && (
              <p style={{ ...menor, margin: 0, padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', maxWidth: '80ch', lineHeight: 1.5 }}>
                {t('projects.design.tab.step3.health.howTo')}
              </p>
            )}
            {resultadoDelLote && !lote && (
              <p style={{ ...menor, margin: 0, padding: 'var(--evo-space-2) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', maxWidth: '80ch' }}>
                {t('projects.design.tab.step3.health.batchSummary', resultadoDelLote)}
              </p>
            )}

            {/* La lista. La pregunta que la parte no es de qué fuente vino ni
                si lo arreglo yo: es si es algo que hay que HACER o algo que hay
                que SABER. Lo segundo no es una tarea y no compite por el lugar. */}
            <div style={{ padding: '0 var(--evo-space-4)' }}>
              {paraHacer.length > 0
                ? (
                  <>
                    <div style={{ ...REJILLA_DE_FILA, alignItems: 'center', padding: 'var(--evo-space-2) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
                      <div style={etiqueta}>{t('projects.design.tab.step3.health.col.severity')}</div>
                      <div style={etiqueta}>{t('projects.design.tab.step3.health.col.finding')}</div>
                      <div style={etiqueta}>{t('projects.design.tab.step3.health.col.action')}</div>
                    </div>
                    {paraHacer.map(h => (
                      <FilaHallazgo
                        // El borrador guardado va en la clave: si la revisión
                        // deja uno nuevo, la fila se vuelve a montar en su estado.
                        key={`${h.id}:${d.borradores[h.id]?.actualizadoEn ?? ''}`}
                        projectId={projectId}
                        hallazgo={h}
                        registrar={registrar}
                        onCambio={recontar}
                        {...(auditId !== undefined ? { auditId } : {})}
                        {...(d.borradores[h.id] ? { guardado: d.borradores[h.id]! } : {})}
                      />
                    ))}
                  </>
                )
                : <p style={{ ...menor, margin: 0, padding: 'var(--evo-space-3) 0' }}>{t('projects.design.tab.step3.health.nothingToDo')}</p>}

              {/* Lo que Eduardo cerró sin tocar el archivo, con su motivo y la
                  vuelta atrás. Plegado: es historial, no tarea. */}
              {((d.descartados ?? []).length > 0 || paraSaber.length > 0) && (
                <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexWrap: 'wrap', padding: 'var(--evo-space-3) 0' }}>
                  {(d.descartados ?? []).length > 0 && (
                    <Button size="xs" variant="outline" onClick={() => { setVerDescartados(v => !v); }}>
                      {t('projects.design.tab.step3.health.dismissed', { count: d.descartados.length })}
                    </Button>
                  )}
                  {paraSaber.length > 0 && (
                    <Button size="xs" variant="outline" onClick={() => { setVerDiagnostico(v => !v); }}>
                      {verDiagnostico
                        ? t('projects.design.tab.step3.health.hideDiagnostics')
                        : t('projects.design.tab.step3.health.showDiagnostics', { count: paraSaber.length })}
                    </Button>
                  )}
                </div>
              )}
              {verDescartados && (d.descartados ?? []).length > 0 && (
                <Grupo titulo={t('projects.design.tab.step3.health.dismissed', { count: d.descartados.length })} nota={t('projects.design.tab.step3.health.dismissedHint')}>
                  {d.descartados.map(x => (
                    <div key={x.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{x.titulo}</div>
                        <div style={{ ...menor, marginTop: '2px', maxWidth: '80ch' }}>{x.motivo}</div>
                      </div>
                      <Button size="xs" variant="outline" loading={restaurar.isPending} onClick={() => { restaurar.mutate(x.id); }}>
                        {t('projects.design.tab.step3.health.restore')}
                      </Button>
                    </div>
                  ))}
                </Grupo>
              )}
              {verDiagnostico && paraSaber.length > 0 && (
                <Grupo titulo={t('projects.design.tab.step3.health.toKnow')} nota={t('projects.design.tab.step3.health.toKnowHint')}>
                  {paraSaber.map(h => <FilaHallazgo key={h.id} projectId={projectId} hallazgo={h} />)}
                </Grupo>
              )}
              <div style={{ height: 'var(--evo-space-2)' }} />
            </div>
          </>
        )
        : (
          <p style={{ margin: 0, padding: 'var(--evo-space-4)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', maxWidth: '70ch' }}>
            {todoSinMedir
              ? t('projects.design.tab.step3.health.idle')
              : t('projects.design.tab.step3.health.clean')}
          </p>
        )}
    </DetailSection>
  );
}
